import type { HeaderMap, UserSourceHeaderName } from './config/sourceHeaders';
import type { AnalyticSourceParamInput } from './config/sourceParams';
import { parseCookies } from 'nookies';
import { USER_SOURCE_HEADER_NAMES } from './config/sourceHeaders';
import { USER_SOURCE_RULES } from './config/sourceRules';
import { readExpiredCustomerSourceHeadersFromStorage } from './customerSourceStorage';
import { getFirstSearchParamValue } from './parsers/sourceResolvers';
import { sanitizeTrackingValue } from './trackingValue';

export const USER_SOURCE_IDENTIFIERS = Object.fromEntries(
  USER_SOURCE_HEADER_NAMES.map(headerName => [headerName, USER_SOURCE_RULES[headerName].paramName]),
) as Record<UserSourceHeaderName, AnalyticSourceParamInput>;

// Parser advertisement source headers from URL query parameters based on predefined rules and pass them to request headers.
export function parseUserSourceHeaders(url: string): HeaderMap {
  const { searchParams } = new URL(url);
  const headers: HeaderMap = {};
  const expiredHeaders = readExpiredCustomerSourceHeadersFromStorage();

  for (const [headerName, rule] of Object.entries(USER_SOURCE_RULES)) {
    if (expiredHeaders.includes(headerName)) {
      continue;
    }

    const resolve = rule.resolver ?? getFirstSearchParamValue;
    const value = resolve(searchParams, rule.paramName);

    const normalizedValue = sanitizeTrackingValue(value);
    if (normalizedValue) {
      // URLSearchParams already returns decoded values.
      headers[headerName] = normalizedValue;
    }
  }
  return headers;
}

export function isValidFbcFromCookies(): boolean {
  const cookies = parseCookies();
  if (!cookies._fbc) {
    return false;
  }

  let fbcValue = cookies._fbc;
  if (fbcValue.startsWith('_fbc=')) {
    fbcValue = fbcValue.slice('_fbc='.length);
  }

  if (!fbcValue) {
    return false;
  }

  fbcValue = decodeURIComponent(fbcValue);

  const match = fbcValue.match(/^fb\.(\d+)\.(\d+)\.([^.]+)$/);
  if (!match) {
    return false;
  }

  const [, version, timestamp, clickId] = match;
  return Boolean(version && timestamp && clickId);
}

/**
 * Prefer the `_fbc` cookie value when valid; otherwise fallback to
 * `localStorage.customer_source_headers["x-fbc"]` (fbclid).
 *
 * Returns `undefined` when no valid value can be resolved.
 */
export function resolveFbcHeaderValue(fbcCookieValue: string | null): string | undefined {
  if (!fbcCookieValue) {
    return undefined;
  }

  if (isValidFbcFromCookies()) {
    return fbcCookieValue;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const customerSourceHeadersLS = window.localStorage.getItem('customer_source_headers');
    const parsedCustomerSourceHeaders = customerSourceHeadersLS
      ? (JSON.parse(customerSourceHeadersLS) as Record<string, unknown>)
      : null;

    const fallbackFbc = parsedCustomerSourceHeaders?.['x-fbc'];
    return typeof fallbackFbc === 'string' && fallbackFbc ? fallbackFbc : undefined;
  } catch {
    return undefined;
  }
}
